import { type SelectedItems } from "@heroui/react";
import { useMemo } from "react";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import type { CalendarFilterOption } from "./types";

type OptionSelectProps = {
  label: string;
  allLabel: string;
  emptyLabel: string;
  options: CalendarFilterOption[];
  selected: number[];
  onChange: (ids: number[]) => void;
  error?: string;
  isSearchable?: boolean;
  dataIdentifier: string;
};

export function OptionSelect({
  label,
  allLabel,
  emptyLabel,
  options,
  selected,
  onChange,
  error,
  isSearchable = true,
  dataIdentifier,
}: OptionSelectProps) {
  const items = useMemo(
    () =>
      options.map((option) => ({
        key: option.id.toString(),
        label: option.label,
      })),
    [options],
  );

  const selectedKeys = useMemo(() => selected.map(String), [selected]);

  return (
    <LimeSelect
      label={label}
      aria-label={label}
      data-identifier={dataIdentifier}
      placeholder={emptyLabel}
      variant="bordered"
      items={items}
      selectionMode="multiple"
      showSelectAllToggle
      // Clearing is a valid drawer state: `error` reports it and Save blocks on
      // it, which beats silently leaving one option selected.
      disallowEmptySelection={false}
      isSearchable={isSearchable}
      // HeroUI's popover transition drops a blank frame at the end of the open
      // animation and flashes one opaque frame on close; with four selects in
      // one drawer that reads as flicker.
      popoverProps={{ disableAnimation: true }}
      value={selectedKeys}
      onMultipleItemChange={(keys) => onChange(keys.map(Number))}
      error={error}
      renderValue={(rendered) =>
        renderSelection(rendered, items.length, allLabel)
      }
    />
  );
}

// Listing every label overflows the trigger once a few are picked, so only the
// first two are named.
function renderSelection(
  rendered: SelectedItems<object>,
  total: number,
  allLabel: string,
): string {
  if (rendered.length === total) return allLabel;

  const shown = rendered.slice(0, 2).map((item) => item.textValue);
  const rest = rendered.length - shown.length;

  return rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
}
